import { NAV_PATHS } from "./api";
import { groupDigits } from "./cell_formatter";
import messages from "./messages";

/**
 * A row action on the Latest by fund tab (Sync, or Delete on UAT) is one value:
 *
 *   null
 *   | { action: "sync", phase: "confirm" | "running", row }
 *   | { action: "delete", phase: "confirm", row, dateFrom: Date | null, dateTo: Date | null }
 *   | { action: "delete", phase: "running", row }
 *   | { action, phase: "done", row, summary } | { action, phase: "failed", row, message }
 *
 * One value rather than a flag per row or per action because be-admin runs one dashboard sync or
 * delete at a time behind one flag (a second one gets 409), so there is never more than one fund
 * and one action in flight to keep track of.
 */

/**
 * The sync_by values be-admin writes when it takes a row from Portal: the cronjob, this dashboard,
 * and the CLI. A fund whose newest row says anything else holds rows Portal may not have.
 */
const PORTAL_SYNC_BY = ["cronjob", "admin_dashboard", "manual"];

/** Whether the row's newest NAV was written by hand rather than synced from Portal. */
export function isHandInserted(row) {
  return Boolean(row.last_sync_by) && !PORTAL_SYNC_BY.includes(row.last_sync_by);
}

/** The range the delete actually covered, as be-admin echoed it back. */
function deletedRangeText({ date_from: from, date_to: to }, intl) {
  if (from && to) {
    return intl.formatMessage(messages.deleteRangeBetween, { from, to });
  }
  if (from) {
    return intl.formatMessage(messages.deleteRangeFrom, { from });
  }
  if (to) {
    return intl.formatMessage(messages.deleteRangeUntil, { to });
  }
  return intl.formatMessage(messages.deleteRangeAll);
}

/** What differs between the row actions; the run around them is the same for both. */
export const RUN_ACTIONS = {
  sync: {
    endpoint: NAV_PATHS.fundNavHistorySync,
    loading: messages.loadingSync,
    doneTitle: messages.syncDoneTitle,
    failedTitle: messages.syncFailedTitle,
    unknownOutcome: messages.syncUnknownOutcome,
    doneText: (row, summary, intl) =>
      intl.formatMessage(messages.syncDoneBody, {
        fundCode: row.fund_code,
        upserted: groupDigits(summary.rows_upserted),
        skipped: groupDigits(summary.rows_skipped)
      }),
    // be-admin answers 200 with status false when any chunk failed, naming those funds.
    incompleteText: (row, data, intl) => {
      const failed = data.failed_fund_nos || [];
      return intl.formatMessage(messages.syncFailedFunds, {
        fundCode: row.fund_code,
        fundNos: failed.length > 0 ? failed.join(", ") : row.fund_no
      });
    }
  },
  delete: {
    endpoint: NAV_PATHS.fundNavHistoryDelete,
    loading: messages.loadingDelete,
    doneTitle: messages.deleteDoneTitle,
    failedTitle: messages.deleteFailedTitle,
    unknownOutcome: messages.deleteUnknownOutcome,
    doneText: (row, summary, intl) =>
      intl.formatMessage(messages.deleteDoneBody, {
        fundCode: row.fund_code,
        rows: groupDigits(summary.rows_deleted),
        range: deletedRangeText(summary, intl)
      }),
    incompleteText: (row, data, intl) =>
      intl.formatMessage(messages.deleteIncomplete, { fundCode: row.fund_code })
  }
};

/** The phase a running action whose request resolved moves to. HTTP 200 is not success on its own. */
export function resultPhase({ action, row }, result, intl) {
  if (result.status) {
    return { action, phase: "done", row, summary: result.data };
  }
  return {
    action,
    phase: "failed",
    row,
    message: RUN_ACTIONS[action].incompleteText(row, result.data, intl)
  };
}

/**
 * The phase a running action whose request threw moves to. be-admin's 400 and 409 bodies say
 * exactly what is wrong ("another sync is running", "fund not found"), so that text is shown as it
 * came. Without a body (a gateway timeout, a dropped connection) the run may still have gone
 * through on the server, so the operator is told the outcome is unknown rather than that it failed.
 */
export function errorPhase({ action, row }, error, intl) {
  if (error && error.detail) {
    return { action, phase: "failed", row, message: error.detail };
  }
  const reason =
    error && error.status
      ? intl.formatMessage(messages.errorStatus, { status: error.status })
      : intl.formatMessage(messages.syncNoResponse);
  return {
    action,
    phase: "failed",
    row,
    message: intl.formatMessage(RUN_ACTIONS[action].unknownOutcome, {
      fundCode: row.fund_code,
      reason
    })
  };
}

/** The body of the result dialog for a done or failed phase. */
export function outcomeText(run, intl) {
  if (run.phase === "failed") {
    return run.message;
  }
  return RUN_ACTIONS[run.action].doneText(run.row, run.summary, intl);
}

/**
 * The tab's rows: the summary rows, plus a no-NAV row for every master fund the summary lacks.
 *
 * The summary is built from funds_nav_history, so a fund with no NAV row at all is absent from it,
 * and its Sync button with it. Deleting a fund's whole history would otherwise leave no way to sync
 * it back from this tab.
 */
export function withFundsWithoutNav(summaryRows, masterFunds) {
  const covered = new Set(summaryRows.map(row => Number(row.fund_no)));
  const missing = masterFunds
    .filter(fund => !covered.has(Number(fund.fund_no)))
    .map(fund => ({ fund_no: fund.fund_no, fund_code: fund.fund_code, row_count: 0 }));
  return summaryRows.concat(missing).sort((a, b) => Number(a.fund_no) - Number(b.fund_no));
}
